import React from 'react';
import { Box, Chip, Typography, alpha } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TimerIcon from '@mui/icons-material/Timer';
import {
  formatCountdown,
  formatLotEndDate,
  getLotTransitionMessage,
  type ActiveSalesPhaseInfo,
} from './lotDisplayUtils';
import { useLotCountdown } from './useLotCountdown';

interface ActiveLotBannerProps {
  phase?: ActiveSalesPhaseInfo | null;
  /** Nome do lote quando vem só do ingresso (modo batched). */
  lotName?: string;
  endSaleDate?: string;
  compact?: boolean;
  accentColor?: string;
}

const ActiveLotBanner: React.FC<ActiveLotBannerProps> = ({
  phase,
  lotName,
  endSaleDate,
  compact = false,
  accentColor = '#4b1b7a',
}) => {
  const resolvedPhase: ActiveSalesPhaseInfo | null = phase ?? (lotName ? { name: lotName, endSaleDate } : null);
  const endDate = resolvedPhase?.endSaleDate || endSaleDate;
  const transition = resolvedPhase ? getLotTransitionMessage(resolvedPhase) : null;
  const countdownEndDate =
    resolvedPhase && transition?.showCountdown && endDate ? endDate : null;
  const countdown = useLotCountdown(countdownEndDate);

  if (!resolvedPhase?.name) return null;

  const { showCountdown, subtitle } = transition!;

  return (
    <Box
      sx={{
        mb: compact ? 1.5 : 2,
        p: compact ? 1.25 : 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(accentColor, 0.22)}`,
        bgcolor: alpha(accentColor, 0.06),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <ConfirmationNumberIcon sx={{ fontSize: 18, color: accentColor }} />
        <Chip
          label={`Lote: ${resolvedPhase.name}`}
          size="small"
          sx={{
            fontWeight: 700,
            bgcolor: accentColor,
            color: '#fff',
            height: 24,
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: showCountdown && countdown && !countdown.expired ? 1 : 0 }}>
        {subtitle}
      </Typography>

      {showCountdown && countdown && !countdown.expired && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TimerIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography
            variant={compact ? 'body2' : 'subtitle1'}
            fontWeight={800}
            sx={{ fontFamily: 'monospace', color: accentColor, letterSpacing: 0.5 }}
          >
            {formatCountdown(countdown)}
          </Typography>
          {endDate && (
            <Typography variant="caption" color="text.secondary">
              até {formatLotEndDate(endDate)}
            </Typography>
          )}
        </Box>
      )}

      {showCountdown && countdown?.expired && (
        <Typography variant="caption" color="warning.main" fontWeight={600}>
          Prazo deste lote encerrou — o próximo pode abrir em breve.
        </Typography>
      )}
    </Box>
  );
};

export default ActiveLotBanner;
